/**
 * useUndoStack — the ONE undo stack, now with redo.
 *
 * Merges the three forked implementations in this repo
 * (CANVAS-AUTHORING-ADVERSARIAL-REVIEW-2026-09 §H), keeping the best of each:
 *
 *   - GoalCanvas: entries join the stack only AFTER the host confirms the
 *     write, and a serialising busy flag so two inverses for one row can't
 *     race. That correctness is the reason this hook exists at all.
 *   - TaskBubbleCanvas (`useUndoableTasks`): a plain exported hook with a
 *     `canUndo` flag, reused across three surfaces.
 *   - BiomeWorldV1 (Lab): the only one with REDO. Adopted here.
 *
 * Design notes that matter:
 *
 *   - `run()` takes the forward action and its inverse together, so redo is
 *     just the forward action replayed. This works because every board
 *     operation is ABSOLUTE ("set colour to sky", "move to x,y", "set status
 *     paused"), never relative — replaying is idempotent.
 *   - Any new action clears the redo stack, the universal convention.
 *   - `retire(id)` drops entries aimed at an object that no longer exists.
 *     A hard-deleted sticky comes back from undo under a NEW id, so entries
 *     holding the old one would 404.
 */
import { useCallback, useRef, useState } from 'react';
export function useUndoStack({ max = 40 } = {}) {
    const undoRef = useRef([]);
    const redoRef = useRef([]);
    const busyRef = useRef(false);
    // One state bump republishes depth + labels + busy together; the refs are
    // what the gesture handlers read synchronously.
    const [, bump] = useState(0);
    const [busy, setBusy] = useState(false);
    const sync = useCallback(() => bump((n) => n + 1), []);
    const push = useCallback((entry) => {
        const st = undoRef.current;
        st.push(entry);
        if (st.length > max)
            st.shift();
        redoRef.current = []; // a new action forks the timeline
        sync();
    }, [max, sync]);
    const run = useCallback((entry, forward, after) => {
        void Promise.resolve(forward())
            .then(() => {
            push({ ...entry, redo: entry.redo || forward });
            if (after)
                after();
        })
            .catch(() => { });
    }, [push]);
    const step = useCallback((from) => {
        if (busyRef.current)
            return;
        const src = from === 'undo' ? undoRef : redoRef;
        const dst = from === 'undo' ? redoRef : undoRef;
        const entry = src.current.pop();
        sync();
        if (!entry)
            return;
        const action = from === 'undo' ? entry.undo : entry.redo;
        if (!action)
            return;
        busyRef.current = true;
        setBusy(true);
        void Promise.resolve(action())
            // Only offer the round trip when the other direction can actually run.
            // Undoing a hard delete has no coherent redo, and an entry parked on
            // the redo stack that quietly does nothing is worse than no button.
            .then(() => { if (from === 'redo' || entry.redo)
            dst.current.push(entry); })
            .catch(() => { })
            .finally(() => { busyRef.current = false; setBusy(false); sync(); });
    }, [sync]);
    const undo = useCallback(() => step('undo'), [step]);
    const redo = useCallback(() => step('redo'), [step]);
    const retire = useCallback((id) => {
        const dead = (e) => e.id === id || !!e.ids?.includes(id);
        undoRef.current = undoRef.current.filter((e) => !dead(e));
        redoRef.current = redoRef.current.filter((e) => !dead(e));
        sync();
    }, [sync]);
    const clear = useCallback(() => {
        undoRef.current = [];
        redoRef.current = [];
        sync();
    }, [sync]);
    const top = undoRef.current[undoRef.current.length - 1];
    const topRedo = redoRef.current[redoRef.current.length - 1];
    return {
        canUndo: undoRef.current.length > 0,
        canRedo: !!topRedo?.redo,
        count: undoRef.current.length,
        undoLabel: top?.label ?? null,
        redoLabel: topRedo?.label ?? null,
        busy,
        run,
        push,
        undo,
        redo,
        retire,
        clear,
    };
}
export default useUndoStack;
